import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

import { setActions } from '../slices/kpis';
import { UserInChat } from '../../types/chat';

export const kpisService = createApi({
  reducerPath: 'kpisService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Warnings', 'USER_INFO'],

  endpoints: (builder) => ({
    getStats: builder.query({
      query: () => ({
        url: `/v1/admin-actions/stats`,
        method: 'GET',
      }),
    }),

    getCustomers: builder.query({
      query: ({ page, search, userType }) => ({
        url: `/v1/admin-actions`,
        method: 'GET',
        params: { page, search, limit: 10, userType },
      }),
    }),

    getCustomerInfo: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis/profile-info/${id}`,
        method: 'GET',
      }),

      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const data = await queryFulfilled;
        const actions = {
          canWarn: data?.data?.payload?.actions?.canWarn,
          canTempSuspend: data?.data?.payload?.actions?.canTempSuspend,
          canPermSuspend: data?.data?.payload?.actions?.canPermSuspend,
        };

        dispatch(setActions(actions));
      },

      providesTags: ['USER_INFO'],
    }),

    getClientPies: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis/client-kpi/${id}`,
        method: 'GET',
      }),
    }),

    getTalentPies: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis/talent-kpi/${id}`,
        method: 'GET',
      }),
    }),

    getDetailedKPIChart: builder.query({
      query: ({ id, type }) => ({
        url: `/jobs/v1/customer-kpis/detailed-kpi/${id}/${type}`,
        method: 'GET',
      }),
    }),

    getMostHired: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis/talent-job-type/${id}`,
        method: 'GET',
      }),
    }),

    getMostPublished: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis/client-job-type/${id}`,
        method: 'GET',
      }),
    }),

    getUserWarnings: builder.query({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis-actions/${id}`,
        method: 'GET',
      }),
      providesTags: ['Warnings'],
    }),

    sendWarning: builder.mutation({
      query: (body) => ({
        url: `/jobs/v1/customer-kpis-actions/warning`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res, err) => {
        if (err || !res) return [];
        else return ['Warnings', 'USER_INFO'];
      },
    }),

    deleteWarning: builder.mutation({
      query: ({ itemId, profileId }) => ({
        url: `/jobs/v1/customer-kpis-actions`,
        method: 'DELETE',
        body: {
          profileId,
          id: itemId,
        },
      }),

      invalidatesTags: (res, err) => {
        if (err || !res) return [];
        else return ['Warnings', 'USER_INFO'];
      },
    }),

    suspend: builder.mutation({
      query: (body) => ({
        url: `/jobs/v1/customer-kpis-actions/suspension`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res, err) => {
        if (err || !res) return [];
        else return ['Warnings', 'USER_INFO'];
      },
    }),

    createChatKPI: builder.mutation<{ payload: UserInChat }, string>({
      query: (id) => ({
        url: `/jobs/v1/customer-kpis-actions/create-chat`,
        method: 'POST',
        body: {
          profileId: id,
        },
      }),
      invalidatesTags: (res, err) => {
        if (err || !res) return [];
        else return ['Warnings'];
      },
    }),
  }),
});

export const {
  useGetStatsQuery,
  useGetCustomersQuery,
  useGetCustomerInfoQuery,
  useGetClientPiesQuery,
  useGetTalentPiesQuery,
  useGetDetailedKPIChartQuery,
  useGetMostHiredQuery,
  useGetMostPublishedQuery,
  useSendWarningMutation,
  useGetUserWarningsQuery,
  useDeleteWarningMutation,
  useSuspendMutation,
  useCreateChatKPIMutation,
} = kpisService;
